import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ClassificationService } from '../../../../../services/classification/classification.service';
import { IClassification, IClassificationResult, ISaveClassification } from '../../../../../models/classification.model';
import { environment } from '../../../../../../environments/environment';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, filter, finalize, lastValueFrom, map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { ConsultationRecommendationService } from '../../../../../services/consultation-recommendation/consultation-recommendation.service';
import { CVANotificationService } from '../../../../../services/cva-notification/cva-notification.service';
import { CVA_CONSTANTS } from '../../../../../util/constants';
import { NotificationClassEnum } from '../../../../../models/notification-class.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationDialogService } from '../../../../../shared/confirmation-dialog/services/confirmation-dialog.service';

@Component({
  standalone: false,
  selector: 'cva-consultation-classification',
  templateUrl: './consultation-classification.component.html',
  styleUrl: './consultation-classification.component.scss',

})
export class ConsultationClassificationComponent implements OnInit, OnDestroy {
  @Input() patientId: string;
  @Input() practitionerId: string;
  @Input() notesApproved = false;

  classifications: IClassification[] = [];
  classificationSearchText = '';
  cvaUI_url = environment.cvaUI_url;
  showAddForm = false;
  isLoading = false;
  isSearchLoading = false;
  noSearchResults = false;
  lookupResults: IClassification[] = [];

  private readonly classificationSearchTrigger$ = new Subject<string>();
  private readonly selectedLookupCodes = new Set<string>();
  private readonly selectedLookupItems = new Map<string, IClassification>();

  private readonly _unsubscribeAll = new Subject<void>();

  constructor(
    private readonly recommendationService: ConsultationRecommendationService,
    private readonly classificationService: ClassificationService,
    private readonly cvaNotificationService: CVANotificationService,
    private readonly confirmationDialogService: ConfirmationDialogService,

  ) {

  }

  ngOnInit(): void {
    if (environment.RECOMMENDATIONS.CLASSIFICATIONS) {
      this.listenToGenerateClassifications();
    }

    this.classificationService.classification$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(classifications => this.classifications = classifications);

    this.classificationSearchTrigger$
      .pipe(
        takeUntil(this._unsubscribeAll),
        debounceTime(300),
        map((searchText) => searchText.trim()),
        distinctUntilChanged(),
        switchMap((searchText) => {
          if (!searchText) {
            this.lookupResults = [];
            this.noSearchResults = false;
            this.isSearchLoading = false;
            return of({ response: [] });
          }

          this.isSearchLoading = true;
          this.noSearchResults = false;

          return this.classificationService.getClassificationList(searchText)
            .pipe(
              catchError((err) => {
                this.isSearchLoading = false;
                console.error('[Child] getClassificationList() failed', err);
                this.cvaNotificationService.showNotification(
                  CVA_CONSTANTS.MESSAGE.CLASSIFICATION_FETCH_ERROR,
                  NotificationClassEnum.ERROR
                );
                return of({ response: [] });
              })
            );
        })
      )
      .subscribe({
        next: (res) => {
          this.isSearchLoading = false;

          let classificationList = res.response || [];
          classificationList = classificationList.sort((a, b) => this.customSort(a.code, b.code));

          const existingCodes = new Set(this.classifications.map((classification) => classification.code));
          this.lookupResults = classificationList.filter((classification) => !existingCodes.has(classification.code));

          this.noSearchResults = this.classificationSearchText.trim().length > 0 && this.lookupResults.length === 0;
        }
      });
  }

  toggleCheckbox(index: number) {
    this.classificationService.toggleItem(index);
  }

  get isSubmitDisabled(): boolean {
    return !this.classifications.some(item => item.checked && !item.isSaved);
  }

  get selectedLookupCount(): number {
    return this.selectedLookupCodes.size;
  }

  onClassificationSearchChange(searchText: string): void {
    this.classificationSearchTrigger$.next(searchText);
  }

  searchClassifications(): void {
    this.classificationSearchTrigger$.next(this.classificationSearchText);
  }

  toggleLookupSelection(code: string, checked: boolean): void {
    if (checked) {
      this.selectedLookupCodes.add(code);
      const selectedClassification = this.lookupResults.find((item) => item.code === code);
      if (selectedClassification) {
        this.selectedLookupItems.set(code, selectedClassification);
      }
      return;
    }

    this.selectedLookupCodes.delete(code);
    this.selectedLookupItems.delete(code);
  }

  isLookupItemSelected(code: string): boolean {
    return this.selectedLookupCodes.has(code);
  }

  addSelectedLookupToRecommended(): void {
    this.mergeSelectedLookupSelections();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByClassification(index: number, item: { classification: IClassification; index: number }): string {
    return `${item.classification.code}-${item.index}`;
  }

  trackByLookupCode(index: number, classification: IClassification): string {
    return `${classification.code}-${index}`;
  }

  listenToGenerateClassifications() {
    this.recommendationService.generateNotesTrigger$
      .pipe(
        takeUntil(this._unsubscribeAll),
        // Get the current failed state when the trigger happens
        switchMap((payload) =>
          this.recommendationService.generateNotesFailed$.pipe(
            take(1),
            map((failed) => ({ failed, payload }))
          )
        ),
        // Skip API call if parent already failed
        filter(({ failed }) => !failed),
        filter(({ payload }) =>
          payload.isTranscriptionChanged ||
          this.classifications.length === 0
        ),
        // Proceed with the API call
        switchMap(({ payload }) => {
          this.isLoading = true;
          return this.classificationService.generateClassifications(payload.transcript)
            .pipe(
              finalize(() => {
                this.isLoading = false;   // always runs (success, error, cancel)
              }),
              catchError((err) => {
                console.error('[Child] generateClassifications() failed', err);
                this.classificationService.setClassifications([], true);
                return of({ response: [] })
              })
            )
        }),
        // Wait until parent signals it's ready before using the response
        switchMap((res) =>
          combineLatest([
            this.recommendationService.generateNotesReady$,
            this.recommendationService.generateNotesFailed$
          ]).pipe(
            filter(([ready, failed]) => ready || failed),
            take(1),
            map(([ready]) => ready ? res : { response: [] })
          )
        )
      )
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          let classificationList = res.response;
          classificationList = classificationList.sort((a, b) => this.customSort(a.code, b.code));
          this.classificationService.setClassifications(classificationList, true);
        }
      });
  }

  async postChecked(): Promise<void> {
    this.mergeSelectedLookupSelections();

    const filteredTasks = this.classifications
      .map((task, index) => ({ ...task, index }))
      .filter(task => task.checked && !task.isSaved)
      .map(({ code, display, index }) => ({ code, display, index }));

    if (!filteredTasks.length) {
      return;
    }

    if (!this.notesApproved) {
      const confirmed = await lastValueFrom(
        this.confirmationDialogService.openConfirmDialog(
          'Do you want to post classifications without approving notes?'
        )
      );
      if (confirmed !== true) {
        return;
      }
    }

    const payload: ISaveClassification = {
      conditions: JSON.parse(JSON.stringify(filteredTasks)),
      patientId: this.patientId,
      practitionerId: this.practitionerId
    }

    this.classificationService.save(payload)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({
        next: (response: IClassificationResult) => {
          response.success.forEach((index) => {
            if (this.classifications[index]) {
              this.classifications[index].isSaved = true;
              this.classifications[index].checked = false;
            }
          });

          response.error.forEach((index) => {
            if (this.classifications[index]) {
              this.classifications[index].checked = false;
            }
          });

          if (response.error.length === 0) {
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.CONDITION_ADDED_SUCCESSFULLY,
              NotificationClassEnum.SUCCESS
            );
          } else {
            this.cvaNotificationService.showNotification(
              `${response.error.length} ${CVA_CONSTANTS.MESSAGE.CONDITION_ADDED_ERR}`,
              NotificationClassEnum.ERROR
            );
          }

          this.classificationService.setClassifications(JSON.parse(JSON.stringify(this.classifications)));
          if (response.success.length > 0) {
            this.classificationService.notifyClassificationChanged();
          }
        },
        error: (errorResponse: HttpErrorResponse) => {
          let errorMessage: string = '';
          if (errorResponse.error?.error) {
            errorMessage = errorResponse.error.error.errorMessage;
          }
          this.cvaNotificationService.showNotification(
            errorMessage || CVA_CONSTANTS.MESSAGE.save_err_soap_notes,
            NotificationClassEnum.ERROR
          );
        }
      }))
  }

  private mergeSelectedLookupSelections(): void {
    if (!this.selectedLookupCodes.size) {
      return;
    }

    const existingCodes = new Set(this.classifications.map((classification) => classification.code));
    const selectedLookupClassifications = Array.from(this.selectedLookupItems.values());

    const newSelections = selectedLookupClassifications
      .filter((classification) => !existingCodes.has(classification.code))
      .map((classification) => ({
        ...classification,
        checked: true,
        isSaved: false,
      }));

    this.classifications = [
      ...newSelections,
      ...this.classifications.map((classification) => (
        this.selectedLookupCodes.has(classification.code) && !classification.isSaved
          ? { ...classification, checked: true }
          : classification
      ))
    ];

    this.classificationService.setClassifications([...this.classifications]);
    this.selectedLookupCodes.clear();
    this.selectedLookupItems.clear();
    this.lookupResults = [];
    this.classificationSearchText = '';
    this.noSearchResults = false;
  }

  customSort(a: string, b: string) {
    const isUppercaseA = /^[A-Z]/.test(a);
    const isLowercaseA = /^[a-z]/.test(a);
    const isNumberA = /^\d/.test(a);

    const isUppercaseB = /^[A-Z]/.test(b);
    const isLowercaseB = /^[a-z]/.test(b);
    const isNumberB = /^\d/.test(b);

    // 1. Prioritize Uppercase-starting strings
    if (isUppercaseA && !isUppercaseB) return -1;
    if (!isUppercaseA && isUppercaseB) return 1;

    // 2. Prioritize Lowercase-starting strings among the non-Uppercase ones
    if (isLowercaseA && !isLowercaseB) return -1;
    if (!isLowercaseA && isLowercaseB) return 1;

    // 3. Prioritize Number-starting strings among the remaining (should be the last group)
    // This step is somewhat redundant if the previous two handled all other cases
    // but ensures numbers come after mixed cases (if they exist)
    if (isNumberA && !isNumberB) return -1;
    if (!isNumberA && isNumberB) return 1;

    // Fallback: If both fall into the same category (or neither of the main three), 
    // sort them alphabetically/lexicographically within their group.
    return a.localeCompare(b);
  }

  ngOnDestroy(): void {
    this.classificationSearchTrigger$.complete();
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
